import { Bundle } from "@/pages/bundles/types/bundle";
import { z } from "zod";

export const CouponTypeEnum = {
  CAMPAIGN: "CAMPAIGN",
  INTERNAL: "INTERNAL",
  COMMUNITY: "COMMUNITY",
  B2B: "B2B",
  B2B2C: "B2B2C",
  AFFILIATE: "AFFILIATE",
  SERVICE_ADJUSTMENT: "SERVICE_ADJUSTMENT",
} as const;

export const StatusEnum = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
} as const;

export const couponSchema = z
  .object({
    // trim() first: a whitespace-only name must fail min(1), and edge spaces
    // must not create a visually-identical duplicate of an existing coupon
    // (the server's duplicate check is an exact string match).
    name: z.string().trim().min(1, "Coupon name is required"),
    first_time_user_only: z.boolean().default(false),
    type: z.enum([
      "CAMPAIGN",
      "INTERNAL",
      "COMMUNITY",
      "B2B",
      "B2B2C",
      "AFFILIATE",
      "SERVICE_ADJUSTMENT",
    ] as const),
    status: z.enum(["ACTIVE", "INACTIVE"] as const),
    currency: z.string().min(1, "Coupon Currency is required"),
    discount_cap: z.number().int().optional().nullable(),
    discount_min: z.number().int().min(1).optional().nullable(),
    discount_amount: z.number().int().min(1).optional().nullable(),
    discount_percentage: z
      .number()
      .min(1, "Discount percentage must be at least 1")
      .max(100, "Discount percentage cannot be more than 100")
      .optional()
      .nullable(),
    start_date: z.string().datetime(),
    end_date: z.string().datetime().optional().nullable(),
    bundle_ids: z.array(z.string()).optional(),
    description: z
      .string()
      .optional()
      .nullable()
      .transform((val) => (val === "" ? null : val)),
    usage_limit: z.number().int().min(1).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    // Check if both discount fields are empty — `== null` on purpose so it
    // catches undefined too (untouched fields on a fresh create form).
    if (data.discount_amount == null && data.discount_percentage == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either discount percentage or fixed amount must be provided",
        path: ["discount_amount"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either discount percentage or fixed amount must be provided",
        path: ["discount_percentage"],
      });
    }
  });

export type CouponData = {
  id: string;
  created_at: string;
  bundles?: Bundle[];
} & z.infer<typeof couponSchema>;

export type CouponFormData = z.infer<typeof couponSchema>;
